const week2 = {
  number: 2,
  title: "The Inner House",
  theme: "The architecture of your mind",
  modules: [
    {
      id: "2.1",
      title: "The Mind as Dwelling",
      content: `Your mind is not a single thing. It is a structure—a dwelling with multiple layers, rooms, and inhabitants.

This is not merely a metaphor. It is an architectural description of how the mind actually organizes itself.

Think about it: You have experienced moments when you felt like "a different person." You have said things and later wondered, "Why did I say that?" You have wanted two contradictory things at the same time. You have surprised yourself with your own reactions.

These experiences make no sense if you are a single, unified self. They make perfect sense if you are a dwelling inhabited by multiple patterns, each with its own perspective, fears, and preferred responses.

**The Inner House** is the framework for understanding this architecture.

The House has layers: a Basement, a Ground Floor, an Upper Floor. It has rooms that serve specific functions: a Back Room where overwhelming experiences are stored, a Hallway where different parts of you encounter each other, an Attic where old versions of yourself wait.

The House has residents: stable psychological patterns formed through developmental stages, attachment experiences, repeated environments, and major emotional events. These residents are not personalities or characters—they are functional units of survival and meaning-making.

Why does architecture matter?

Because **you cannot navigate a house you cannot see.**

When you do not understand the structure of your own mind, you are constantly surprised by your reactions, confused by your contradictions, and unable to create the conditions for movement.

When you understand the architecture, you can work with it. You can recognize which resident is speaking. You can understand why certain rooms are activated. You can create conditions that allow the house to function rather than fight itself.

This week, you will learn to see your own dwelling.`
    },
    {
      id: "2.2",
      title: "The Basement",
      content: `The Basement is the deepest layer of the Inner House. It holds the earliest residents—the patterns formed before language, before conscious memory, before you had words for anything at all.

The Basement operates through sensation: panic, urgency, longing, attachment, safety, threat. These are not thoughts. They are states—full-body experiences that precede any reasoning about them.

This is crucial: **Because Basement residents developed before logic, they cannot be persuaded with logic.**

You cannot argue yourself out of a panic response. You cannot think your way through an attachment wound. You cannot reason with the part of you that learned, before you could speak, that the world was unsafe.

Basement residents believe patterns, not arguments. They learned through experience, and they update only through experience. This is why talking about your problems rarely solves them at the Basement level. The talking happens on the Ground Floor; the problem lives in the Basement.

When the Basement activates, the entire house shakes.

You know this experience. Something triggers you—a tone of voice, a perceived rejection, a sense of being trapped—and suddenly you are not your usual self. Your capacity shrinks. Your perspective narrows. You react in ways that later seem disproportionate.

This is Basement activation. The oldest parts of you have taken over because they detected something that matches an ancient pattern of threat.

The Basement is not your enemy. It is trying to protect you using the only tools it has: tools formed before you had any other options. The problem is not that the Basement exists. The problem is when the Basement runs the house.

Understanding the Basement means:

- Recognizing when you are activated at this level
- Not expecting logic to work when you are there
- Creating safety first, understanding second
- Respecting the protective intention behind the reaction`
    },
    {
      id: "2.3",
      title: "The Ground Floor",
      content: `The Ground Floor is where you meet the world.

This layer holds your competence, habits, routines, social identity, work self, and publicly presented self. It is where you function—where you get things done, interact with others, maintain the structures of daily life.

The Ground Floor often acts as if it is in charge. It is the part of you that makes plans, sets goals, manages your calendar, and presents a coherent face to the world.

But the Ground Floor rarely leads the house. It manages; it does not govern.

Think of it this way: The Ground Floor is like a capable office manager. It keeps things running, handles the logistics, maintains the systems. But when something activates the Basement, the office manager is overruled. When the Upper Floor has a vision, the office manager must figure out how to execute it.

The Ground Floor's strength is competence. It knows how to do things. It has developed skills, routines, and habits that allow you to function without conscious thought about every small action.

The Ground Floor's limitation is that it can become disconnected from both the depths below and the aspirations above. It can keep you functioning without asking whether the functioning serves any larger purpose. It can maintain a life that looks successful from the outside while the Basement panics and the Upper Floor's dreams go unheard.

Many people live primarily on the Ground Floor. They are functional, competent, productive—and vaguely dissatisfied. They have mastered the how of living without addressing the why.

The Ground Floor is necessary. You cannot navigate without competence. But competence alone does not produce direction. It does not address the Basement's ancient fears. It does not listen to the Upper Floor's pull toward something more.

The Ground Floor's job is to support navigation—not to determine destination.`
    },
    {
      id: "2.4",
      title: "The Upper Floor",
      content: `The Upper Floor is the aspirational layer of your Inner House.

Here lives the self you are trying to become: ambition, purpose, long-term identity, creative impulses, values, and vision. The Upper Floor is where direction lives—the pull toward something beyond your current position.

The Upper Floor generates direction, but it cannot enforce it.

This is the Upper Floor's fundamental limitation. It can see what you might become. It can sense the pull toward growth, meaning, contribution. But it cannot make this happen through will alone.

The Upper Floor needs the Ground Floor's competence to translate vision into action. It needs the Basement to feel safe enough to support forward movement. It needs the whole house to coordinate.

When the Upper Floor is disconnected from the rest of the house, you experience a particular kind of suffering: you can see what you want but cannot seem to move toward it. Your vision is clear, but your action is blocked. You know who you want to become, but you keep being who you have always been.

This is not a failure of will. It is a failure of coordination. The Upper Floor is proposing a direction that the rest of the house is not ready to support.

Sometimes the Basement is too activated—too much fear, too little safety—to allow the risk of growth. Sometimes the Ground Floor is too committed to existing patterns—too much competence in the old way—to allow new approaches.

The Upper Floor's role is to keep the direction alive. To hold the vision even when movement seems impossible. To remind the rest of the house that there is something beyond the current moment.

But the Upper Floor must also listen. It must recognize when its aspirations are disconnected from what the whole system can actually support. The goal is not to silence the Upper Floor or to let it override everything else. The goal is coordination.`
    },
    {
      id: "2.5",
      title: "Back Room, Hallway, Attic",
      content: `Beyond the three main floors, the Inner House has three additional spaces that shape your experience.

**The Back Room**

The Back Room is where experiences too overwhelming to integrate are stored. Humiliation, betrayal, failure, fear, grief, shame—the moments that exceeded your capacity at the time they occurred.

These experiences do not disappear. They wait.

When the Back Room fills, it leaks into the rest of the house, distorting decision-making and destabilizing identity. You find yourself reacting to present situations with the intensity of past wounds. You feel disproportionate emotion and do not know why.

The Back Room cannot be emptied by ignoring it. It empties slowly, through safe acknowledgment and gradual integration—through the process of allowing what was too much to become bearable, a little at a time.

**The Hallway**

The Hallway is where residents encounter each other. It is the space of internal negotiation—and internal conflict.

Indecision, self-doubt, impulsivity, and contradiction all occur in the Hallway. This is where one part of you wants safety and another wants growth. Where one part wants connection and another wants independence. Where you feel pulled in multiple directions at once.

The Hallway is both where movement gets blocked and where movement begins. It is the space of friction—productive or unproductive depending on how the friction is handled.

**The Attic**

The Attic stores remnants of past versions of identity: old narratives, outdated beliefs, outgrown coping strategies, and shed ambitions.

The Attic contains both debris that must be cleared and patterns that can be reclaimed. Old strengths you forgot you had. Dreams you abandoned too quickly. Parts of yourself that served you once and might serve you again in new form.

Not everything in the Attic should stay there. Not everything should be retrieved. The Attic requires periodic visits—a willingness to sort through what was, keeping what still serves, releasing what no longer does.

**Understanding these spaces matters because they explain experiences that otherwise seem inexplicable: Why certain triggers produce outsized reactions (Back Room leakage). Why decisions feel impossible (Hallway congestion). Why you sometimes feel nostalgia for a self you thought you had outgrown (Attic echoes).**`
    }
  ],
  reflection: {
    prompt: "Walk through your own Inner House. Which floor feels most active right now? Which room do you avoid? What might you find there if you looked?",
    placeholder: "Write your reflection here. Remember, this is not about getting it right—it's about beginning to see the architecture of your own experience."
  }
};

export default week2;
